import React, { useState } from "react";
import { TextMessage } from "@lango/api/src/queries/chat/types";
import { Language } from "@lango/api/src/queries/config/types";
import { SpeakerIcon } from "@/components/common/icons/SpeakerIcon";
import { TranslateIcon } from "@/components/common/icons/TranslateIcon";
import { Button } from "@/components/common/Button";
import { useBot } from "@/api/useBot";
import { useTokens } from "@/api/useTokens";
import { useTranslate } from "@/api/useTranslate";
import { isEmoji, isSpecialCharacter } from "@/utils/utils";

import styles from "./BotTextMessage.module.css";

type Props = {
  data: TextMessage;
  onPlayText: (text: string) => void;
  onWordPress: (word: string, language: Language) => void;
  onTranslation?: (messageId: string, translation: string) => void;
};

export const BotTextMessage: React.FC<Props> = ({
  data,
  onPlayText,
  onWordPress,
  onTranslation,
}) => {
  const { text, language } = data;
  const { data: bot } = useBot();
  const [isTranslationOpen, setIsTranslationOpen] = useState(false);
  const { data: tokens } = useTokens(text, language || bot!.language);
  const {
    data: translateData,
    translate,
    isLoading: isTranslateLoading,
  } = useTranslate();

  React.useEffect(() => {
    if (translateData?.data && onTranslation) {
      onTranslation(data.id, translateData.data);
    }
  }, [translateData, data.id, onTranslation]);

  const onTranslate = () => {
    setIsTranslationOpen((prev) => !prev);
    if (!translateData) {
      translate(text, language || bot!.language);
    }
  };

  const onSoundPress = () => {
    if (onPlayText) {
      onPlayText(text);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.bubble}>
        {!tokens && <div className={styles.messageText}>{text}</div>}
        {tokens && (
          <div className={styles.tokens}>
            {tokens.map((word, index) => (
              <React.Fragment key={`${word}-${index}`}>
                <button
                  disabled={isEmoji(word) || isSpecialCharacter(word)}
                  className={styles.token}
                  onClick={() =>
                    onWordPress(word, data.language || bot!.language)
                  }
                >
                  {word}
                </button>{" "}
              </React.Fragment>
            ))}
          </div>
        )}
        {(isTranslateLoading || translateData) && isTranslationOpen && (
          <div>
            <div className={styles.line} />
            <div className={styles.translateheader}>
              Traduction (par Google):
            </div>
            {translateData && (
              <>
                <div className={styles.translateText}>{translateData.data}</div>
              </>
            )}
            {isTranslateLoading && (
              <div className={styles.translateText}>{text}</div>
            )}
          </div>
        )}
        <div className={styles.actionButtonWrap}>
          <Button
            variant="clean"
            className={styles.speaker}
            onClick={onSoundPress}
          >
            <SpeakerIcon className={styles.icon} />
          </Button>
          <Button
            variant="clean"
            className={styles.translate}
            onClick={onTranslate}
          >
            <TranslateIcon className={styles.icon} />
          </Button>
        </div>
      </div>
    </div>
  );
};
